
import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Upload, Check, X } from 'lucide-react';

interface CameraModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPhotoCapture: (photoUrl: string) => void;
}

export const CameraModal: React.FC<CameraModalProps> = ({
  isOpen,
  onClose,
  onPhotoCapture
}) => {
  const [capturedPhoto, setCapturedPhoto] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const photoUrl = event.target?.result as string;
        setCapturedPhoto(photoUrl);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleConfirm = () => {
    if (capturedPhoto) {
      onPhotoCapture(capturedPhoto);
      onClose();
    }
  };

  const handleCancel = () => {
    setCapturedPhoto(null);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Submit a Photo</DialogTitle>
          <DialogDescription>
            Upload an existing photo from your device.
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex flex-col items-center justify-center p-2">
          {capturedPhoto ? (
            <>
              <div className="relative rounded-lg overflow-hidden mb-4">
                <img 
                  src={capturedPhoto} 
                  alt="Captured" 
                  className="w-full h-auto" 
                />
              </div>
              
              <div className="flex space-x-4">
                <Button variant="outline" onClick={handleCancel}>
                  <X className="h-4 w-4 mr-1" />
                  Retake
                </Button>
                <Button onClick={handleConfirm}>
                  <Check className="h-4 w-4 mr-1" />
                  Use Photo
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="bg-muted rounded-lg w-full aspect-square flex items-center justify-center mb-4">
                <Upload className="h-12 w-12 text-muted-foreground" />
              </div>
              
              <div className="flex flex-col space-y-2 w-full">
                <Button 
                  onClick={handleUploadClick} 
                  className="w-full"
                >
                  <Upload className="h-4 w-4 mr-1" />
                  Upload Your Photo
                </Button>
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  accept="image/*"
                  onChange={handleFileChange}
                />
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
